import { Card, Code, Flex, Separator, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { JsonViewer } from 'components/common/json/json-viewer';
import { SubSectionBox } from 'components/sections/feature-demo/sub-section-box';
import env from 'config';
import { MachineContext } from 'contexts/machine.context';
import { format, parseISO } from 'date-fns';
import {
  MachineSettingsQueryParams,
  MachineSettingsSection,
} from 'enums/machine-settings';
import { t } from 'i18next';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { Controller } from 'lib_ts/enums/routing/controllers';
import { MainRoutes } from 'lib_ts/enums/routing/routes/main';
import { RealTimeApiRequest } from 'lib_ts/interfaces/real-time-api/request';
import { useContext, useEffect, useMemo, useState } from 'react';
import { paths } from 'routes/paths';

const RealTimeMessage = ({ message }: { message: RealTimeApiRequest }) => {
  const [showMessage, setShowMessage] = useState(false);
  return (
    <Card
      onClick={() => setShowMessage((prev) => !prev)}
      className="cursor-pointer"
    >
      <Flex direction="column" gap="2">
        <Flex align="center" justify="between">
          <Text weight="medium">
            <strong>{message.machineID}</strong> - {message.description}
          </Text>
          <Text>{format(parseISO(message.timestamp), 'h:mm:ss aaa')}</Text>
        </Flex>
        {showMessage && <JsonViewer value={message} />}
      </Flex>
    </Card>
  );
};

export const RealTimeApiTest = () => {
  const [messages, setMessages] = useState<RealTimeApiRequest[]>([]);
  const { machine } = useContext(MachineContext);

  useEffect(() => {
    const onMessage = (event: CustomEvent) => {
      const message = event.detail as RealTimeApiRequest;
      setMessages((prev) => [message, ...prev]);
    };

    WebSocketHelper.on(WsMsgType.S2U_RealTimeTest, onMessage);
    return () => {
      WebSocketHelper.remove(WsMsgType.S2U_RealTimeTest, onMessage);
    };
  }, []);

  const configUrl = useMemo(() => {
    const params = new URLSearchParams({
      [MachineSettingsQueryParams.MachineID]: machine.machineID,
      [MachineSettingsQueryParams.SectionID]:
        MachineSettingsSection.Configuration,
    });

    return `${env.self_url}${paths.admin.machines}?${params.toString()}`;
  }, [env.self_url, machine.machineID]);

  const webhookUrl = `${env.server_url}/${Controller.Main}/${MainRoutes.RealTimeApiTest}`;

  return (
    <ErrorBoundary identifier="RealTimeApiTest">
      <SubSectionBox title="Real Time API Test" source="real-time-api">
        <Text>
          This section listens for real time API messages sent during BP.
          <br />
          To test this feature,{' '}
          <a href={configUrl} target="_blank" rel="noopener noreferrer">
            configure your current machine
          </a>{' '}
          to use the following URL as a real time API endpoint via the Details
          configuration tab.
        </Text>

        <Flex gap="2" align="center" style={{ width: '100%' }}>
          <Code
            color={RADIX.COLOR.NEUTRAL}
            onClick={() => {
              navigator.clipboard.writeText(webhookUrl);
              NotifyHelper.success({
                message_md: t('common.x-copied-to-clipboard', {
                  x: webhookUrl,
                }),
              });
            }}
            className="cursor-pointer"
          >
            {webhookUrl}
          </Code>
        </Flex>

        {messages.length > 0 && <Separator size="4" />}

        {messages.map((message) => (
          <RealTimeMessage message={message} key={message.timestamp} />
        ))}
      </SubSectionBox>
    </ErrorBoundary>
  );
};
